import { useEffect, useState } from "react";
import { Alert, Button, Card, Switch, Table, Tag } from "antd";
import { PlusOutlined, ReloadOutlined, EditOutlined } from "@ant-design/icons";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import { useModal } from "../../core/context/ModalContext";
import { useUser } from "../../core/context/UserContext";
import AppConfig from "../../core/service/AppConfig";
import { matchesSearch } from "../../core/utils/text-search";
import AppointmentTypeModal from "./AppointmentTypeModal";
import { FilterActions, SearchCard } from "../../core/components/quick-filter/QuickFilterParts";

/**
 * Randevu türleri (Sistem → Tanımlar). Rota APPOINTMENT_TYPE_MANAGER ile açılır.
 * Silme yok: tür randevulara ve kurumlara bağlı.
 */
export default function AppointmentTypeApp() {
    const [types, setTypes] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const { openModal, closeModal } = useModal();
    const { firmSettings } = useUser();

    const load = async () => {
        setLoading(true);
        try {
            const res = await ApiService.Appointment.GetAppointmentTypes();
            if (res.success) setTypes(res.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = `${t('Appointment Types')} - ${AppConfig.getHisTitle()}`;
        load();
    }, []);

    const openEdit = (type) => {
        openModal(
            type ? `${t('Edit Appointment Type')} - ${type.name}` : t('New Appointment Type'),
            <AppointmentTypeModal type={type} onSuccess={() => { closeModal(); load(); }} />,
            () => { }
        );
    };

    // E-Nabız gönderimi listeden tek tıkla aç/kapat; diğer alanlar olduğu gibi gönderilir.
    const toggleEnabiz = async (row, checked) => {
        setSavingId(row.id);
        try {
            const res = await ApiService.AppointmentType.EditAppointmentType(row.id, {
                name: row.name,
                enabizEnabled: checked,
                overrideInstitutionPrice: row.overrideInstitutionPrice,
                hidePatientBalance: row.hidePatientBalance ?? 0,
            });
            if (res.success) {
                setTypes(types.map(x => x.id === row.id ? { ...x, enabizEnabled: checked } : x));
            }
        } finally {
            setSavingId(null);
        }
    };

    const filtered = search ? types.filter(x => matchesSearch(x.name, search)) : types;

    return (
        <SimpleLayout fullWidth={true}>
            <div className="qf-row qf-row-top">
                <SearchCard value={search} onChange={setSearch} />
            </div>
            <FilterActions>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>{t('New')}</Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={load}>{t('Refresh')}</Button>
            </FilterActions>
            <Card title={t('Appointment Types')}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('Automatic E-Nabız sending requires the unit, the institution and the appointment type to be enabled together.')}
                />
                <Table
                    rowKey="id"
                    size="small"
                    loading={loading}
                    dataSource={filtered}
                    pagination={false}
                    columns={[
                        {
                            dataIndex: 'name', key: 'name', title: t('Name'),
                            defaultSortOrder: 'ascend',
                            sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', firmSettings?.locale)
                        },
                        {
                            dataIndex: 'enabizEnabled', key: 'enabizEnabled', title: t('E-Nabız Sending Status'), width: 180,
                            render: (v, row) => (
                                <Switch
                                    size="small"
                                    checked={v}
                                    loading={savingId === row.id}
                                    onChange={(checked) => toggleEnabiz(row, checked)}
                                />
                            )
                        },
                        {
                            dataIndex: 'overrideInstitutionPrice', key: 'overrideInstitutionPrice', title: t('Override Institution Price'), width: 180,
                            render: (v) => v ? <Tag color="blue">{t('Yes')}</Tag> : <Tag>{t('No')}</Tag>
                        },
                        {
                            dataIndex: 'hidePatientBalance', key: 'hidePatientBalance', title: t('Hide Patient Balance Threshold'), width: 200,
                            render: (v) => v > 0 ? v : '-'
                        },
                        {
                            key: 'actions', title: '#', width: 60,
                            render: (_, row) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
                        },
                    ]}
                />
            </Card>
        </SimpleLayout>
    );
}
